// Progress dashboard (spec §11.5.10).
//
// Surfaces:
//   • Level progress + total XP
//   • Streak calendar (GitHub-style, 12 weeks, gold intensity = XP earned that day)
//   • XP breakdown by category (horizontal bars)
//   • Achievement gallery (unlocked = gold-toned card; locked = grey silhouette;
//     comingSoon = hidden until the feature suite ships)
//   • Streak recovery banner (when previousStreakDays > 0 and currentStreakDays = 1)
//
// Intrinsic learners get the quiet mastery view — only the dimension /
// adaptation surfaces, no XP/badge content. We render a short note
// and link them to /profile/evolution instead.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Fingerprint, ChevronsDown, Target, Library, Flame, Award, Crown,
  Mountain, Compass, Mic, Network, AlertTriangle, Sparkles, Lock,
} from 'lucide-react';
import { useMotivationalDriverVisibility } from '@shared/hooks/useMotivationalDriverVisibility';
import {
  subscribeGamificationSummary,
  ensureGamificationDoc,
  loadRecentDailyActivity,
} from '@shared/services/gamification';
import {
  type DailyActivityRecord,
  type UserGamification,
  levelFromXp,
} from '@shared/types/gamification';
import {
  ACHIEVEMENTS_CATALOG,
  type AchievementDefinition,
  type GamificationAction,
} from '@shared/constants/points-config';

const ICONS: Record<string, typeof Sparkles> = {
  first_print: Fingerprint,
  deep_diver: ChevronsDown,
  test_pilot: Target,
  polyglot: Library,
  week_warrior: Flame,
  month_master: Award,
  perfect_run: Crown,
  path_pioneer: Mountain,
  self_aware: Compass,
  voice_learner: Mic,
  knowledge_mapper: Network,
  contradiction_finder: AlertTriangle,
};

interface ProgressDashboardProps {
  userId: string;
}

export function ProgressDashboard({ userId }: ProgressDashboardProps) {
  const visibility = useMotivationalDriverVisibility(userId);
  const [summary, setSummary] = useState<UserGamification | null>(null);
  const [daily, setDaily] = useState<DailyActivityRecord[]>([]);

  useEffect(() => {
    let active = true;
    void ensureGamificationDoc(userId);
    const unsub = subscribeGamificationSummary(userId, (s) => {
      if (active) setSummary(s);
    });
    void loadRecentDailyActivity(userId, 84).then((records) => {
      if (active) setDaily(records);
    });
    return () => {
      active = false;
      unsub();
    };
  }, [userId]);

  if (visibility.intrinsic) {
    return (
      <div className="rounded-xl border border-dark-border bg-dark-surface p-6">
        <h2 className="font-heading text-lg font-bold text-content">Quiet mode</h2>
        <p className="text-sm font-body text-content-secondary mt-2">
          Your CogniPrint motivation profile leans intrinsic, so we keep XP and badges out of your view.
          Use{' '}
          <Link to="/profile/evolution" className="text-indigo-400 underline">
            Profile Evolution
          </Link>{' '}
          to see how your learning is shifting.
        </p>
      </div>
    );
  }

  if (!summary) {
    return <div className="text-content-muted text-sm font-body">Loading progress…</div>;
  }

  const level = levelFromXp(summary.totalXp);
  const breakdown = aggregateBreakdown(daily);
  const streakBroken = summary.previousStreakDays > 0 && summary.currentStreakDays <= 1;

  return (
    <div className="space-y-5">
      {streakBroken && (
        <div className="rounded-xl border border-dark-border bg-dark-subtle p-4 flex items-start gap-3">
          <Flame className="text-gold-500 shrink-0 mt-0.5" size={20} />
          <div>
            <div className="font-heading text-sm font-bold text-content">
              You were on a {summary.previousStreakDays}-day streak!
            </div>
            <div className="text-sm font-body text-content-secondary mt-0.5">
              Studying any concept today starts a new one.
            </div>
          </div>
        </div>
      )}

      <section className="rounded-xl border border-dark-border bg-dark-surface p-5">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs font-body uppercase tracking-wider text-content-muted">
              Level {level.current.level}
            </div>
            <div className="font-heading text-2xl font-bold text-content">{level.current.title}</div>
          </div>
          <div className="text-right">
            <div className="font-heading text-2xl font-bold text-gold-500">
              {summary.totalXp.toLocaleString()}
              <span className="text-sm font-body text-content-muted ml-1">XP</span>
            </div>
            {level.next && (
              <div className="text-xs font-body text-content-muted">
                {level.xpToNextLevel.toLocaleString()} to {level.next.title}
              </div>
            )}
          </div>
        </div>
        {level.next && (
          <div className="mt-3 h-2 rounded-full bg-dark-bg overflow-hidden">
            <div
              className="h-full bg-gold-500 rounded-full transition-[width]"
              style={{ width: `${level.progress * 100}%` }}
            />
          </div>
        )}
      </section>

      <section className="rounded-xl border border-dark-border bg-dark-surface p-5">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="font-heading text-base font-bold text-content">Last 12 weeks</h3>
          <div className="flex items-center gap-2 text-xs font-body text-content-muted">
            <span>Less</span>
            {[0, 1, 2, 3, 4].map((i) => (
              <span
                key={i}
                className="inline-block w-3 h-3 rounded-sm"
                style={{ background: heatColor(i / 4) }}
              />
            ))}
            <span>More</span>
          </div>
        </div>
        <StreakCalendar daily={daily} />
      </section>

      <section className="rounded-xl border border-dark-border bg-dark-surface p-5">
        <h3 className="font-heading text-base font-bold text-content mb-3">XP breakdown</h3>
        {breakdown.total === 0 ? (
          <div className="text-sm font-body text-content-muted">
            Complete a node, checkpoint, or recalibration and your XP will start to add up here.
          </div>
        ) : (
          <ul className="space-y-2">
            {breakdown.byCategory.map((row) => (
              <li key={row.label}>
                <div className="flex items-baseline justify-between text-xs font-body text-content-secondary">
                  <span>{row.label}</span>
                  <span className="text-content-muted">
                    {row.xp.toLocaleString()} XP · {Math.round((row.xp / breakdown.total) * 100)}%
                  </span>
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-dark-bg overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${(row.xp / breakdown.total) * 100}%`,
                      background: row.color,
                    }}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-xl border border-dark-border bg-dark-surface p-5">
        <h3 className="font-heading text-base font-bold text-content mb-3">Achievements</h3>
        <AchievementGallery summary={summary} />
      </section>
    </div>
  );
}

// ─── Streak calendar ──────────────────────────────────────────────────

const WEEKS = 12;
const DAYS = WEEKS * 7;

function StreakCalendar({ daily }: { daily: DailyActivityRecord[] }) {
  const byDate = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of daily) m.set(r.date, r.xpEarned);
    return m;
  }, [daily]);

  const cells = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const result: { date: string; xp: number }[] = [];
    for (let i = DAYS - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      result.push({ date: key, xp: byDate.get(key) ?? 0 });
    }
    return result;
  }, [byDate]);

  const max = useMemo(() => {
    let m = 0;
    for (const c of cells) if (c.xp > m) m = c.xp;
    return m || 1;
  }, [cells]);

  // Render as 7 rows × WEEKS columns (Sun..Sat).
  const grid: typeof cells[number][][] = Array.from({ length: 7 }, () => []);
  for (const cell of cells) {
    const dow = new Date(cell.date + 'T00:00:00').getDay();
    grid[dow].push(cell);
  }

  return (
    <div className="overflow-x-auto">
      <div className="inline-grid grid-rows-7 grid-flow-col gap-1" role="img" aria-label="Study activity over the last 12 weeks">
        {grid.flat().map((cell) => {
          const intensity = cell.xp === 0 ? 0 : Math.min(1, 0.2 + cell.xp / max);
          return (
            <span
              key={cell.date}
              title={`${cell.date}: ${cell.xp} XP`}
              className="w-3 h-3 rounded-sm"
              style={{ background: cell.xp === 0 ? 'rgba(108, 92, 231, 0.08)' : heatColor(intensity) }}
            />
          );
        })}
      </div>
    </div>
  );
}

function heatColor(intensity: number): string {
  // Blend indigo (#6C5CE7) → gold (#F0A500) for intensity 0..1.
  if (intensity <= 0) return 'rgba(108, 92, 231, 0.08)';
  const a = 0.25 + intensity * 0.6;
  // Cheap channel mix: low intensity skews indigo, high skews gold.
  const r = Math.round(108 + (240 - 108) * intensity);
  const g = Math.round(92 + (165 - 92) * intensity);
  const b = Math.round(231 + (0 - 231) * intensity);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

// ─── XP breakdown ─────────────────────────────────────────────────────

interface BreakdownRow {
  label: string;
  xp: number;
  color: string;
}

const CATEGORIES: { label: string; actions: GamificationAction[]; color: string }[] = [
  {
    label: 'Onboarding',
    color: '#A594F9',
    actions: ['diagnostic_started', 'diagnostic_completed', 'first_upload', 'first_tutor_query'],
  },
  {
    label: 'Learning',
    color: '#6C5CE7',
    actions: ['node_completed', 'checkpoint_passed', 'checkpoint_perfect', 'content_uploaded', 'path_completed'],
  },
  {
    label: 'Tutor',
    color: '#F0A500',
    actions: [
      'tutor_query', 'tutor_session_completed',
      'tutor_mode_explain', 'tutor_mode_go_deeper', 'tutor_mode_test_me',
      'tutor_mode_connect', 'tutor_mode_simplify',
    ],
  },
  {
    label: 'Recalibration',
    color: '#A39BE0',
    actions: ['recalibration_completed'],
  },
  {
    label: 'Streaks',
    color: '#E8A33D',
    actions: ['daily_login', 'streak_day_added', 'week_streak_bonus', 'month_streak_bonus'],
  },
  {
    label: 'Teams',
    color: '#8B7CE0',
    actions: ['cohort_challenge_joined', 'cohort_challenge_won', 'peer_help_given'],
  },
];

function aggregateBreakdown(
  daily: DailyActivityRecord[],
): { total: number; byCategory: BreakdownRow[] } {
  const totals: Record<string, number> = {};
  for (const day of daily) {
    for (const [action, count] of Object.entries(day.actionCounts)) {
      totals[action] = (totals[action] ?? 0) + count;
    }
  }
  // Convert action counts × point value into XP. Filter out streak day-row
  // double-counting (we logged the daily XP under the action keyed `streak_day_added`
  // already). Use POINTS_FOR via dynamic import isn't worth it — encode here.
  // We just sum daily.xpEarned by category by re-walking action counts.
  let total = 0;
  const byCategory: BreakdownRow[] = CATEGORIES.map((cat) => {
    let xp = 0;
    for (const day of daily) {
      for (const action of cat.actions) {
        const c = day.actionCounts[action] ?? 0;
        xp += c * pointsFor(action);
      }
    }
    total += xp;
    return { label: cat.label, xp, color: cat.color };
  })
    .filter((r) => r.xp > 0)
    .sort((a, b) => b.xp - a.xp);
  return { total, byCategory };
}

// Inlined to avoid circular import — keep in sync with points-config.ts.
function pointsFor(action: string): number {
   
  const map: Record<string, number> = {
    diagnostic_started: 25,
    diagnostic_completed: 100,
    first_upload: 100,
    first_tutor_query: 50,
    node_completed: 25,
    checkpoint_passed: 50,
    checkpoint_perfect: 100,
    content_uploaded: 30,
    tutor_query: 5,
    tutor_session_completed: 25,
    recalibration_completed: 50,
    path_completed: 200,
    tutor_mode_explain: 5,
    tutor_mode_go_deeper: 10,
    tutor_mode_test_me: 15,
    tutor_mode_connect: 10,
    tutor_mode_simplify: 5,
    daily_login: 10,
    streak_day_added: 20,
    week_streak_bonus: 100,
    month_streak_bonus: 500,
    cohort_challenge_joined: 50,
    cohort_challenge_won: 150,
    peer_help_given: 25,
  };
  return map[action] ?? 0;
}

// ─── Achievement gallery ──────────────────────────────────────────────

function AchievementGallery({ summary }: { summary: UserGamification }) {
  const earned = new Set(summary.achievements.map((a) => a.id));
  const visible = ACHIEVEMENTS_CATALOG.filter((a) => !a.comingSoon || earned.has(a.id));
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
      {visible.map((def) => (
        <AchievementCard key={def.id} def={def} unlocked={earned.has(def.id)} />
      ))}
    </div>
  );
}

function AchievementCard({ def, unlocked }: { def: AchievementDefinition; unlocked: boolean }) {
  const Icon = ICONS[def.id] ?? Sparkles;
  const accent = def.tone === 'gold' ? '#F0A500' : '#6C5CE7';
  return (
    <div
      className="rounded-xl border p-3 transition-colors"
      style={{
        borderColor: unlocked ? `${accent}55` : 'rgba(255, 255, 255, 0.06)',
        background: unlocked ? `${accent}14` : 'rgba(255, 255, 255, 0.02)',
        opacity: unlocked ? 1 : 0.55,
      }}
    >
      <div className="flex items-center gap-2">
        <div
          className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
          style={{
            background: unlocked ? `${accent}22` : 'rgba(255, 255, 255, 0.04)',
            border: unlocked ? `1px solid ${accent}55` : '1px solid rgba(255, 255, 255, 0.06)',
          }}
        >
          {unlocked ? (
            <Icon size={16} style={{ color: accent }} />
          ) : (
            <Lock size={14} className="text-content-muted" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-heading text-sm font-bold text-content truncate">{def.title}</div>
          {def.xpReward > 0 && (
            <div className="text-xs font-body text-content-muted">+{def.xpReward} XP</div>
          )}
        </div>
      </div>
      <div className="text-xs font-body text-content-secondary mt-2 line-clamp-2 min-h-[2.5em]">
        {def.description}
      </div>
    </div>
  );
}
